// Auto-generated by `pnpm tl`. Do not edit by hand.
// Source: backend ProblemRegistry.All — served from /api/v1/errors.
import { ProblemSlug } from './generated-types';

export interface ProblemTypeMetadata {
  code: ProblemSlug;
  status: number;
  title: string;
  summary: string;
  userMessage: string;
  extensions: readonly string[] | null;
}

export const PROBLEM_CATALOGUE: Readonly<Record<ProblemSlug, ProblemTypeMetadata>> = {
  [ProblemSlug.ArtifactNotFound]: {
    code: ProblemSlug.ArtifactNotFound,
    status: 404,
    title: 'Artifact not found',
    summary: 'The requested transaction artifact (PDF, preview image, JSON output) is not available.',
    userMessage: 'The requested file is not available.',
    extensions: null,
  },
  [ProblemSlug.ExportDestinationMismatch]: {
    code: ProblemSlug.ExportDestinationMismatch,
    status: 409,
    title: 'Export destination mismatch',
    summary: 'The team\'s export destination does not route to this endpoint, or the destination it names is no longer configured. A team exports either to Hazcheck Validate or to a webhook, never both, so exactly one export endpoint applies.',
    userMessage: 'This team\'s export destination does not match this action.',
    extensions: null,
  },
  [ProblemSlug.ExtractionFailed]: {
    code: ProblemSlug.ExtractionFailed,
    status: 422,
    title: 'Extraction failed',
    summary: 'The extraction pipeline ran but did not produce a usable result. Inspect the `errorCategory` extension to determine retryability.',
    userMessage: 'Extraction failed.',
    extensions: ['errorCategory'],
  },
  [ProblemSlug.ExtractionModelCodeNameTaken]: {
    code: ProblemSlug.ExtractionModelCodeNameTaken,
    status: 409,
    title: 'Extraction model code name taken',
    summary: 'Another registry model already uses this code name; code names must be unique.',
    userMessage: 'That model code name is already in use.',
    extensions: null,
  },
  [ProblemSlug.ExtractionModelInUse]: {
    code: ProblemSlug.ExtractionModelInUse,
    status: 409,
    title: 'Extraction model in use',
    summary: 'The model cannot be deleted: it is a seeded default or is still selected by one or more teams. Reassign those teams first.',
    userMessage: 'This model is in use and cannot be deleted.',
    extensions: null,
  },
  [ProblemSlug.ExtractionModelNotFound]: {
    code: ProblemSlug.ExtractionModelNotFound,
    status: 404,
    title: 'Extraction model not found',
    summary: 'The requested extraction model config id does not exist in the registry.',
    userMessage: 'Extraction model not found.',
    extensions: null,
  },
  [ProblemSlug.FeedbackValidationFailed]: {
    code: ProblemSlug.FeedbackValidationFailed,
    status: 400,
    title: 'Feedback validation failed',
    summary: 'The feedback submission could not be accepted (missing user context, empty body, etc.).',
    userMessage: 'Feedback could not be submitted.',
    extensions: null,
  },
  [ProblemSlug.FileValidationFailed]: {
    code: ProblemSlug.FileValidationFailed,
    status: 400,
    title: 'File validation failed',
    summary: 'An uploaded file failed pre-extraction checks (missing, oversize, or wrong content type).',
    userMessage: 'The uploaded file is not valid.',
    extensions: null,
  },
  [ProblemSlug.Forbidden]: {
    code: ProblemSlug.Forbidden,
    status: 403,
    title: 'Forbidden',
    summary: 'Authenticated but lacking the role required for this operation.',
    userMessage: 'You don\'t have permission to perform this action.',
    extensions: null,
  },
  [ProblemSlug.GeminiTimeout]: {
    code: ProblemSlug.GeminiTimeout,
    status: 504,
    title: 'Gemini timeout',
    summary: 'The upstream Gemini AI service did not respond within the configured timeout window. Safe to retry.',
    userMessage: 'The AI extraction service took too long to respond. Please try again.',
    extensions: null,
  },
  [ProblemSlug.GeminiUnavailable]: {
    code: ProblemSlug.GeminiUnavailable,
    status: 503,
    title: 'Gemini unavailable',
    summary: 'The upstream Gemini AI service returned an error or is currently unreachable. Safe to retry with backoff.',
    userMessage: 'The AI extraction service is temporarily unavailable. Please try again shortly.',
    extensions: null,
  },
  [ProblemSlug.HazcheckNotConfigured]: {
    code: ProblemSlug.HazcheckNotConfigured,
    status: 400,
    title: 'Hazcheck not configured',
    summary: 'The caller\'s team has not configured Hazcheck integration credentials. A Hazcheck administrator must supply ClientId and ClientSecret in team settings.',
    userMessage: 'Hazcheck integration is not configured for your team. Contact Hazcheck support.',
    extensions: null,
  },
  [ProblemSlug.HazcheckTimeout]: {
    code: ProblemSlug.HazcheckTimeout,
    status: 504,
    title: 'Hazcheck timeout',
    summary: 'The upstream Hazcheck service did not respond within the configured timeout window. Safe to retry.',
    userMessage: 'The Hazcheck service took too long to respond. Please try again.',
    extensions: null,
  },
  [ProblemSlug.HazcheckUnavailable]: {
    code: ProblemSlug.HazcheckUnavailable,
    status: 503,
    title: 'Hazcheck unavailable',
    summary: 'The upstream Hazcheck service returned an error or is currently unreachable. Safe to retry with backoff.',
    userMessage: 'The Hazcheck service is temporarily unavailable. Please try again shortly.',
    extensions: null,
  },
  [ProblemSlug.HazcheckValidationFailed]: {
    code: ProblemSlug.HazcheckValidationFailed,
    status: 422,
    title: 'Hazcheck validation failed',
    summary: 'One or more cargo items were rejected by the Hazcheck regulatory engine. The `errors` extension carries field-level messages keyed by JSON path; `cargoIndices` maps each path to its zero-based cargo index.',
    userMessage: 'Some substances failed regulatory validation.',
    extensions: ['errors', 'cargoIndices'],
  },
  [ProblemSlug.InternalServerError]: {
    code: ProblemSlug.InternalServerError,
    status: 500,
    title: 'Internal server error',
    summary: 'An unexpected error occurred. The `traceId` extension correlates with backend logs for support.',
    userMessage: 'An unexpected error occurred. Please try again.',
    extensions: null,
  },
  [ProblemSlug.JobAlreadyClaimed]: {
    code: ProblemSlug.JobAlreadyClaimed,
    status: 409,
    title: 'Job already claimed',
    summary: 'Another worker claimed this job before the request reached us. Retry against a fresh job id.',
    userMessage: 'This job is already being processed by another worker.',
    extensions: null,
  },
  [ProblemSlug.JobNotCancellable]: {
    code: ProblemSlug.JobNotCancellable,
    status: 409,
    title: 'Job not cancellable',
    summary: 'The job is in a terminal state and can no longer be cancelled.',
    userMessage: 'This job cannot be cancelled in its current state.',
    extensions: null,
  },
  [ProblemSlug.JobNotFound]: {
    code: ProblemSlug.JobNotFound,
    status: 404,
    title: 'Job not found',
    summary: 'The extraction job id does not exist or is not visible to the caller.',
    userMessage: 'Job not found.',
    extensions: null,
  },
  [ProblemSlug.JobNotRetryable]: {
    code: ProblemSlug.JobNotRetryable,
    status: 409,
    title: 'Job not retryable',
    summary: 'The job is in a state that does not allow retry.',
    userMessage: 'This job cannot be retried in its current state.',
    extensions: null,
  },
  [ProblemSlug.OpenAiUnavailable]: {
    code: ProblemSlug.OpenAiUnavailable,
    status: 503,
    title: 'OpenAI unavailable',
    summary: 'The upstream OpenAI service (secondary extraction provider) returned an error or is currently unreachable. Safe to retry with backoff.',
    userMessage: 'The AI extraction service is temporarily unavailable. Please try again shortly.',
    extensions: null,
  },
  [ProblemSlug.QwenResponseInvalid]: {
    code: ProblemSlug.QwenResponseInvalid,
    status: 502,
    title: 'Qwen response invalid',
    summary: 'The in-house Qwen extraction model returned a 200 whose body could not be parsed into the expected schema — usually a truncated JSON document when output hits the token cap. The verbatim body is saved to the failed_extraction artifact for inspection. Not auto-retried.',
    userMessage: 'The AI extraction service returned an unreadable response. Please try again or contact support.',
    extensions: null,
  },
  [ProblemSlug.QwenSchemaVersionMismatch]: {
    code: ProblemSlug.QwenSchemaVersionMismatch,
    status: 502,
    title: 'Qwen schema version mismatch',
    summary: 'The Qwen extraction proxy is serving a document schema this build\'s mapping was not written against. Mapping a foreign schema silently drops or mis-binds fields, so the extraction is refused outright rather than producing a partially-populated document. Not auto-retried — it clears only when the proxy\'s active prompt and the API\'s compiled schema mapper are realigned.',
    userMessage: 'The AI extraction service is running an incompatible version. Please contact support.',
    extensions: null,
  },
  [ProblemSlug.QwenUnavailable]: {
    code: ProblemSlug.QwenUnavailable,
    status: 503,
    title: 'Qwen unavailable',
    summary: 'The in-house Qwen extraction model (self-hosted via vLLM) returned an error or is currently unreachable. Safe to retry with backoff.',
    userMessage: 'The AI extraction service is temporarily unavailable. Please try again shortly.',
    extensions: null,
  },
  [ProblemSlug.RemovalNotAcknowledged]: {
    code: ProblemSlug.RemovalNotAcknowledged,
    status: 409,
    title: 'Removal not acknowledged',
    summary: 'The save would remove CTUs the document no longer carries, but the request carried no matching acknowledgement of the removals. Preview the merge, confirm the removals, and retry with the acknowledgement. A mismatch also means the validation changed since the preview. Inspect `removedCount` and `targetValidationId`.',
    userMessage: 'This save would remove cargo units from the validation. Review the changes and try again.',
    extensions: ['removedCount', 'targetValidationId'],
  },
  [ProblemSlug.RequestValidationFailed]: {
    code: ProblemSlug.RequestValidationFailed,
    status: 400,
    title: 'Request validation failed',
    summary: 'The request body or query parameters failed model-binding validation. Inspect the `errors` extension for field-level detail.',
    userMessage: 'The request was invalid.',
    extensions: ['errors'],
  },
  [ProblemSlug.ResourceNotFound]: {
    code: ProblemSlug.ResourceNotFound,
    status: 404,
    title: 'Resource not found',
    summary: 'Generic 404 — used when a more specific not-found code does not apply.',
    userMessage: 'The requested resource was not found.',
    extensions: null,
  },
  [ProblemSlug.StorageUnavailable]: {
    code: ProblemSlug.StorageUnavailable,
    status: 503,
    title: 'Storage unavailable',
    summary: 'Object storage (Azure Blob) returned an error. The `storageErrorCode` extension surfaces the underlying provider code where available.',
    userMessage: 'File storage is temporarily unavailable. Please try again.',
    extensions: ['storageErrorCode'],
  },
  [ProblemSlug.SubstanceNotFound]: {
    code: ProblemSlug.SubstanceNotFound,
    status: 404,
    title: 'Substance not found',
    summary: 'No substance in the Hazcheck catalogue matches the supplied id.',
    userMessage: 'Substance not found.',
    extensions: null,
  },
  [ProblemSlug.SupportDeskUnavailable]: {
    code: ProblemSlug.SupportDeskUnavailable,
    status: 503,
    title: 'Support desk unavailable',
    summary: 'The service desk that receives feedback and error reports returned an error, is unconfigured, or was unreachable, so the submission was not delivered. Safe to retry.',
    userMessage: 'We couldn\'t send your message to our support desk just now. Please try again in a moment.',
    extensions: null,
  },
  [ProblemSlug.TeamNotAssigned]: {
    code: ProblemSlug.TeamNotAssigned,
    status: 400,
    title: 'Team not assigned',
    summary: 'The caller\'s account is not assigned to a team. Team-scoped operations cannot proceed until an administrator assigns the caller to a team.',
    userMessage: 'Your account is not assigned to a team. Contact your administrator.',
    extensions: null,
  },
  [ProblemSlug.TeamScopeViolation]: {
    code: ProblemSlug.TeamScopeViolation,
    status: 403,
    title: 'Team scope violation',
    summary: 'Authenticated but targeting a team the caller does not belong to.',
    userMessage: 'You can only access resources belonging to your own team.',
    extensions: null,
  },
  [ProblemSlug.TeamSettingsAlreadyExists]: {
    code: ProblemSlug.TeamSettingsAlreadyExists,
    status: 409,
    title: 'Team settings already exist',
    summary: 'POST cannot create new settings for a team that already has them. Use PUT to update.',
    userMessage: 'Team settings already exist — use update instead of create.',
    extensions: null,
  },
  [ProblemSlug.TeamSettingsNotFound]: {
    code: ProblemSlug.TeamSettingsNotFound,
    status: 404,
    title: 'Team settings not found',
    summary: 'Settings have not yet been created for the requested team.',
    userMessage: 'Team settings have not been configured.',
    extensions: null,
  },
  [ProblemSlug.TransactionExtracting]: {
    code: ProblemSlug.TransactionExtracting,
    status: 409,
    title: 'Transaction extracting',
    summary: 'The transaction is being extracted, and the extraction writes the transaction back when it ends, so this change would be lost. Retry once the transaction\'s status leaves `extracting`.',
    userMessage: 'This document is still being extracted. Try again when it has finished.',
    extensions: null,
  },
  [ProblemSlug.TransactionNotFound]: {
    code: ProblemSlug.TransactionNotFound,
    status: 404,
    title: 'Transaction not found',
    summary: 'The transaction id does not exist or is not visible to the caller.',
    userMessage: 'Transaction not found.',
    extensions: null,
  },
  [ProblemSlug.Unauthenticated]: {
    code: ProblemSlug.Unauthenticated,
    status: 401,
    title: 'Unauthenticated',
    summary: 'Authentication is required. Supply a valid bearer token via the `Authorization` header.',
    userMessage: 'Your session has expired. Please sign in again.',
    extensions: null,
  },
  [ProblemSlug.UserNotFound]: {
    code: ProblemSlug.UserNotFound,
    status: 404,
    title: 'User not found',
    summary: 'The user id does not exist in this team.',
    userMessage: 'User not found.',
    extensions: null,
  },
  [ProblemSlug.ValidationNotFound]: {
    code: ProblemSlug.ValidationNotFound,
    status: 404,
    title: 'Validation not found',
    summary: 'The Hazcheck validation id does not exist upstream, or is not visible to the caller.',
    userMessage: 'That validation no longer exists in Hazcheck Validate.',
    extensions: null,
  },
  [ProblemSlug.WebhookDeliveryAbandoned]: {
    code: ProblemSlug.WebhookDeliveryAbandoned,
    status: 500,
    title: 'Webhook delivery abandoned',
    summary: 'Attempts on this delivery were started but never completed, typically a worker restart while the request was in flight. Rather than keep re-sending to the endpoint indefinitely, delivery stopped. The endpoint may have received earlier copies; `X-Webhook-Delivery-Id` identifies them. The delivery can be retried manually from the webhook deliveries dashboard.',
    userMessage: 'Webhook delivery was stopped after attempts failed to complete.',
    extensions: ['deliveryId'],
  },
  [ProblemSlug.WebhookDeliveryConnectionFailed]: {
    code: ProblemSlug.WebhookDeliveryConnectionFailed,
    status: 502,
    title: 'Webhook delivery connection failed',
    summary: 'The webhook endpoint could not be reached (DNS failure, connection refused, TLS error). Delivery will retry on exponential backoff.',
    userMessage: 'The webhook endpoint could not be reached.',
    extensions: ['deliveryId'],
  },
  [ProblemSlug.WebhookDeliveryCustomerError]: {
    code: ProblemSlug.WebhookDeliveryCustomerError,
    status: 502,
    title: 'Webhook delivery customer error',
    summary: 'The webhook endpoint returned a 5xx response. The customer\'s service may be experiencing an outage. Delivery will retry on exponential backoff.',
    userMessage: 'The webhook endpoint returned a server error.',
    extensions: ['deliveryId', 'customerStatusCode'],
  },
  [ProblemSlug.WebhookDeliveryNotConfigured]: {
    code: ProblemSlug.WebhookDeliveryNotConfigured,
    status: 500,
    title: 'Webhook not configured',
    summary: 'The team\'s webhook was disabled or removed between the event being queued and the send, so nothing was posted to the endpoint. Re-enable the webhook and retry the delivery from the webhook deliveries dashboard.',
    userMessage: 'The webhook was turned off before this delivery could be sent.',
    extensions: ['deliveryId'],
  },
  [ProblemSlug.WebhookDeliveryNotFound]: {
    code: ProblemSlug.WebhookDeliveryNotFound,
    status: 404,
    title: 'Webhook delivery not found',
    summary: 'The delivery attempt id does not exist for this team.',
    userMessage: 'Webhook delivery not found.',
    extensions: null,
  },
  [ProblemSlug.WebhookDeliveryPayloadTooLarge]: {
    code: ProblemSlug.WebhookDeliveryPayloadTooLarge,
    status: 413,
    title: 'Webhook delivery payload too large',
    summary: 'The webhook endpoint returned 413 Payload Too Large. The configured endpoint cannot accept payloads of this size; delivery will not retry successfully without endpoint-side changes or payload-size adjustments.',
    userMessage: 'The webhook payload exceeds the endpoint\'s size limit.',
    extensions: ['deliveryId', 'customerStatusCode'],
  },
  [ProblemSlug.WebhookDeliveryRejected]: {
    code: ProblemSlug.WebhookDeliveryRejected,
    status: 400,
    title: 'Webhook delivery rejected',
    summary: 'The webhook endpoint returned a 4xx response, indicating it rejected the payload (authentication, validation, or schema). Inspect `customerStatusCode` for the precise response status. Retries may not succeed without endpoint-side changes.',
    userMessage: 'The webhook endpoint rejected the delivery.',
    extensions: ['deliveryId', 'customerStatusCode'],
  },
  [ProblemSlug.WebhookDeliveryTimeout]: {
    code: ProblemSlug.WebhookDeliveryTimeout,
    status: 504,
    title: 'Webhook delivery timeout',
    summary: 'The customer\'s webhook endpoint did not respond within the configured timeout window. The delivery will retry on exponential backoff until the configured retry limit.',
    userMessage: 'The webhook endpoint took too long to respond.',
    extensions: ['deliveryId'],
  },
  [ProblemSlug.WebhookDeliveryTokenFailed]: {
    code: ProblemSlug.WebhookDeliveryTokenFailed,
    status: 502,
    title: 'Webhook delivery token failed',
    summary: 'OAuth 2.0 is selected but no access token could be fetched from the configured token URL, so nothing was posted to the endpoint. Check the token URL, client ID and client secret. Delivery will retry on exponential backoff.',
    userMessage: 'Could not get an OAuth token for the webhook.',
    extensions: ['deliveryId'],
  },
};
